import { ChangeDetectorRef, Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ChannelMixer, MAX_CHANNEL_GAIN } from '../channel-mixer';

/**
 * Per-channel meter/volume/mute strips for a ChannelMixer. Meant to be rendered lazily inside a
 * mat-menu (`<ng-template matMenuContent>`), so meters are only sampled while the menu is open.
 */
@Component({
  selector: 'app-channel-strips',
  templateUrl: './channel-strips.component.html',
  styleUrls: ['./channel-strips.component.css']
})
export class ChannelStripsComponent implements OnInit, OnDestroy {
  @Input() mixer: ChannelMixer | null = null;
  /** False while there is no live signal (recording paused, playback stopped); meters then read 0. */
  @Input() active = false;

  readonly maxGainPercent = MAX_CHANNEL_GAIN * 100;
  private meterTimerId: number | null = null;

  constructor(private ngZone: NgZone, private cdRef: ChangeDetectorRef) { }

  ngOnInit(): void {
    // Sampled outside Angular at 10 Hz; only this view is re-rendered, and only when a level changes.
    this.ngZone.runOutsideAngular(() => {
      this.meterTimerId = window.setInterval(() => this.updateLevels(), 100);
    });
  }

  ngOnDestroy(): void {
    if (this.meterTimerId !== null) {
      clearInterval(this.meterTimerId);
      this.meterTimerId = null;
    }
    this.mixer?.clearLevels();
  }

  setGain(index: number, value: number | null): void {
    this.mixer?.setGain(index, (value ?? 100) / 100);
  }

  toggleMute(index: number): void {
    const strip = this.mixer?.strips[index];
    if (strip) {
      this.mixer!.setMuted(index, !strip.muted);
    }
  }

  private updateLevels(): void {
    const mixer = this.mixer;
    if (!mixer) {
      return;
    }
    if (!this.active) {
      if (mixer.strips.some(strip => strip.level > 0)) {
        mixer.clearLevels();
        this.cdRef.detectChanges();
      }
      return;
    }
    // Single channels sit lower than the summed signal, so use the mic-only noise floor.
    if (mixer.updateLevels(0.045)) {
      this.cdRef.detectChanges();
    }
  }
}
